import React, { useState } from 'react';
import {
  Container, Grid, Card, CardContent, CardMedia, Chip,
  Typography, Button, Box, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Dialog, DialogContent, DialogActions, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { isDiscountActive, calculateDiscountedPrice, getDiscountBadge } from '../utils/discountUtils';
import noPhoto from "../assets/no_photo.webp";

const ProductCard = ({ product, addToCart }) => {
  const isDiscounted = isDiscountActive(product);
  const discountedPrice = calculateDiscountedPrice(product);
  const discountBadge = getDiscountBadge(product);
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleAddToCart = () => {
    addToCart(product.id);
    handleClose();
  };

  return (
    <>
     <Grid 
          item 
          key={`${product.id}-${product.created_at}`} 
          xs={12} 
          sm={6} 
          md={4} 
          lg={3}
          sx={{ display: 'flex', justifyContent: 'center' }}
        >
          <Card 
            sx={{ 
              width: '100%',
              maxWidth: 320,
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 12px 28px rgba(0,0,0,0.12)'
              }
            }}
          >
            {/* Discount Badge */}
            {discountBadge && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  zIndex: 1,
                  background: 'linear-gradient(135deg, #ff3e3e 0%, #b71c1c 100%)',
                  color: 'white',
                  fontSize: 12,
                  fontWeight: 'bold',
                  padding: '4px 10px',
                  borderRadius: 12,
                  boxShadow: '0 4px 8px rgba(183,28,28,0.3)'
                }}
              >
                {discountBadge}
              </Box>
            )}

            {/* Product Image */}
            <Box sx={{ position: 'relative', overflow: 'hidden', height: 200 }}>
              <CardMedia
                component="img"
                image={product.imageUrl || noPhoto}
                alt={product.name}
                sx={{ 
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.5s ease',
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              />
              {/* Overlay on image hover */}
              <Box 
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 100%)',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  display: 'flex',
                  alignItems: 'flex-end',
                  p: 2,
                  '&:hover': {
                    opacity: 1
                  }
                }}
              >
                {product.quantity > 0 && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleClickOpen}
                    sx={{
                      minWidth: 'auto',
                      borderRadius: 20,
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      color: 'text.primary',
                      '&:hover': {
                        backgroundColor: 'white'
                      }
                    }}
                  >
                    <VisibilityIcon sx={{ fontSize: 18, mr: 0.5 }} />
                    عرض
                  </Button>
                )}
              </Box>
            </Box>

            <CardContent sx={{ flexGrow: 1, p: 2 }}>
              {/* Category */}
              {product.category && (
                <Typography 
                  variant="caption" 
                  color="primary" 
                  sx={{ 
                    fontWeight: 'medium',
                    display: 'block',
                    mb: 0.5
                  }}
                >
                  {product.category.name}
                </Typography>
              )}
              
              {/* Product Name */}
              <Typography 
                variant="h6" 
                component="div"
                sx={{ 
                  fontWeight: 'bold',
                  mb: 1,
                  lineHeight: 1.3,
                  height: 44,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                {product.name}
              </Typography>
              
              {/* Description */}
              <Typography 
                variant="body2" 
                color="text.secondary" 
                paragraph
                sx={{
                  mb: 2,
                  height: 60,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                {product.description}
              </Typography>
              
              {/* Price Display */}
              <Box sx={{ mt: 'auto' }}>
                {isDiscounted ? (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Typography 
                        variant="h6" 
                        color="error" 
                        fontWeight="bold"
                        sx={{ mr: 1 }}
                      >
                        E.G.P {discountedPrice.toFixed(2)}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          textDecoration: 'line-through', 
                          color: 'text.secondary',
                        }}
                      >
                        E.G.P {product.price.toFixed(2)}
                      </Typography>
                    </Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: 'inline-block',
                        backgroundColor: 'success.light',
                        color: 'success.contrastText',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontWeight: 'bold'
                      }}
                    >
                      وفر {product.discount_percent}%
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="h6" fontWeight="bold">
                    E.G.P {product.price.toFixed(2)}
                  </Typography>
                )}
              </Box>
            </CardContent>
            
            {/* Add to Cart Button */}
            <Box sx={{ p: 2, pt: 0 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => addToCart(product.id)}
                disabled={product.quantity <= 0}
                startIcon={product.quantity > 0 && <AddShoppingCartIcon />}
                sx={{
                  borderRadius: 2,
                  py: 1,
                  fontWeight: 'bold',
                  background: isDiscounted 
                    ? 'linear-gradient(135deg, #ff3e3e 0%, #b71c1c 100%)' 
                    : 'linear-gradient(135deg, #ba7002 0%, #a76707 100%)',
                  boxShadow: isDiscounted 
                    ? '0 4px 12px rgba(183,28,28,0.3)' 
                    : '0 4px 12px rgba(13,71,161,0.3)',
                  '&:hover': {
                    boxShadow: isDiscounted 
                      ? '0 6px 16px rgba(183,28,28,0.4)' 
                      : '0 6px 16px rgba(13,71,161,0.4)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.2s ease',
                  '&:disabled': {
                    background: 'grey.300',
                    color: 'grey.500'
                  }
                }}
              >
                {product.quantity <= 0 ? 'إنتهى من المخزن' : (
                  isDiscounted ? 'أضف إلى السلة - Sale!' : 'أضف إلى السلة'
                )}
              </Button>
            </Box>
          </Card>
        </Grid>


      {/* Product Detail Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth dir='rtl'>
        <DialogContent>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <CardMedia
                component="img"
                height="400"
                image={product.imageUrl || noPhoto}
                alt={product.name}
                sx={{ objectFit: 'contain', borderRadius: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" gutterBottom>
                {product.name}
              </Typography>
              
              {product.category && (
                <Chip 
                  label={product.category.name} 
                  color="primary" 
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              )}
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, textAlign: 'justify' }}>
                  {product.description || 'لا يوجد وصف متوفر لهذا المنتج'}
                </Typography>
              </Box>
              
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  التفاصيل:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {product.details || 'لا توجد تفاصيل إضافية متاحة'}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  طريقة الاستخدام:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {product.usageInstructions || 'يرجى استشارة الطبيب أو الصيدلي قبل الاستخدام'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ mr: 1 }}>
                  الحالة:
                </Typography>
                <Chip 
                  label={product.quantity > 0 ? 'متوفر' : 'غير متوفر'} 
                  color={product.quantity > 0 ? 'success' : 'error'} 
                  size="small" 
                />
              </Box>
              
              {/* Price Display in Dialog */}
              <Box sx={{ mb: 3 }}>
                {isDiscounted ? (
                  <Box>
                    <Typography 
                      variant="h4" 
                      color="error" 
                      fontWeight="bold"
                    >
                      E.G.P {discountedPrice}
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        textDecoration: 'line-through', 
                        color: 'text.secondary'
                      }}
                    >
                      E.G.P {product.price.toFixed(2)}
                    </Typography>
                    <Chip 
                      label={`وفر ${product.discount_percent}%`} 
                      color="success" 
                      sx={{ mt: 1 }} 
                    />
                  </Box>
                ) : (
                  <Typography variant="h4">
                    E.G.P {product.price.toFixed(2)}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} color="secondary">
            إلغاء
          </Button>
          <Button 
            onClick={handleAddToCart} 
            variant="contained" 
            disabled={product.quantity <= 0}
            startIcon={<AddShoppingCartIcon />}
            size="large"
          >
            أضف إلى السلة
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProductCard;